import { Button, Checkbox, Space, Table, Typography, notification } from "antd";
import { useState } from "react";
import useApi from "../../utils/useApi";
import { formatarTelefone } from "../ai-agent/textos";
import PainelDoContato from "../contatos/PainelDoContato";
import { describeError } from "./status";

const { Text } = Typography;

export function chaveDaInscricao(inscricao) {
  return inscricao.phone
    ? `id:${inscricao.phone}`
    : `novo:${inscricao.telefone}`;
}

export function filiaisPossiveis(filiaisDoContato, filiaisDaRotina) {
  if (!filiaisDoContato) return [...filiaisDaRotina];
  const doContato = new Set(filiaisDoContato);
  return filiaisDaRotina.filter((id) => doContato.has(id));
}

export function ordemInicial(contatos, inscricoes) {
  const inscritos = new Set((inscricoes || []).map((i) => i.phone));
  return [...(contatos || [])]
    .sort(
      (a, b) =>
        Number(inscritos.has(b.id)) - Number(inscritos.has(a.id)) ||
        a.nome.localeCompare(b.nome)
    )
    .map((contato) => `id:${contato.id}`);
}

export function inscricoesParaEnvio(inscricoes, filiaisDaRotina) {
  const daRotina = new Set(filiaisDaRotina);
  const itens = (inscricoes || []).map((inscricao) => {
    const possiveis = new Set(
      filiaisPossiveis(inscricao.filiais_do_contato, [...daRotina])
    );
    const filiais = (inscricao.filiais || []).filter((id) => possiveis.has(id));
    const base = { filiais, ativa: inscricao.ativa !== false };
    return inscricao.phone
      ? { ...base, phone: inscricao.phone }
      : { ...base, telefone: inscricao.telefone, nome: inscricao.nome };
  });
  const semFilial = (inscricoes || [])
    .filter((_, indice) => itens[indice].filiais.length === 0)
    .map((inscricao) => inscricao.nome);
  return { itens, semFilial };
}

function linhasDaGrade(contatos, inscricoes, ordem) {
  const porChave = new Map();
  for (const contato of contatos) {
    porChave.set(`id:${contato.id}`, {
      chave: `id:${contato.id}`,
      phone: contato.id,
      nome: contato.nome,
      telefone: contato.telefone,
      ativo: contato.ativo !== false,
      filiaisDoContato: contato.filiais,
      doUsuario: Boolean(contato.usuario),
    });
  }
  for (const inscricao of inscricoes) {
    const chave = chaveDaInscricao(inscricao);
    if (!porChave.has(chave)) {
      porChave.set(chave, {
        chave,
        phone: inscricao.phone,
        nome: inscricao.nome,
        telefone: inscricao.telefone,
        ativo: inscricao.contato_ativo !== false,
        filiaisDoContato: inscricao.filiais_do_contato,
      });
    }
  }
  const chaves = [
    ...ordem.filter((chave) => porChave.has(chave)),
    ...[...porChave.keys()].filter((chave) => !ordem.includes(chave)),
  ];
  return chaves.map((chave) => porChave.get(chave));
}

function novaInscricao(linha, filiais) {
  return {
    phone: linha.phone,
    nome: linha.nome,
    telefone: linha.telefone,
    contato_ativo: linha.ativo,
    filiais_do_contato: linha.filiaisDoContato,
    filiais,
    ativa: true,
  };
}

function comFiliais(inscricoes, linha, filiais) {
  const semLinha = inscricoes.filter(
    (inscricao) => chaveDaInscricao(inscricao) !== linha.chave
  );
  if (!filiais.length) return semLinha;
  if (semLinha.length === inscricoes.length) {
    return [...inscricoes, novaInscricao(linha, filiais)];
  }
  return inscricoes.map((inscricao) =>
    chaveDaInscricao(inscricao) === linha.chave
      ? { ...inscricao, filiais }
      : inscricao
  );
}

function noFormatoDoCatalogo(contato) {
  const telefone = String(contato.telefone || "");
  return {
    id: contato.id,
    nome: contato.nome,
    telefone: telefone.length > 4 ? `•••• ${telefone.slice(-4)}` : "••••",
    client_id: contato.client,
    ativo: contato.ativo !== false,
    usuario: contato.usuario_email,
    filiais: contato.filiais || [],
  };
}

export function mesclarContatos(contatos, salvos) {
  const porId = new Map(salvos.map((contato) => [contato.id, contato]));
  const mesclados = (contatos || []).map((contato) => {
    const salvo = porId.get(contato.id);
    porId.delete(contato.id);
    return salvo || contato;
  });
  return [...mesclados, ...porId.values()];
}

export default function SeletorDeDestinatarios({
  value,
  onChange,
  contatos,
  filiais,
  ordem,
  cliente,
  filiaisDoCliente,
  onContatoSalvo,
}) {
  const { getAgentCatalog, getAgentPhones } = useApi();
  const [salvos, setSalvos] = useState([]);
  const [recente, setRecente] = useState(null);
  const [painel, setPainel] = useState({ aberto: false, contato: null });
  const [contas, setContas] = useState(null);
  const [abrindo, setAbrindo] = useState(null);
  const inscricoes = value || [];
  const idsDaRotina = filiais.map((filial) => filial.id);
  const porChave = new Map(
    inscricoes.map((inscricao) => [chaveDaInscricao(inscricao), inscricao])
  );
  const linhas = linhasDaGrade(
    mesclarContatos(contatos, salvos),
    inscricoes,
    ordem || []
  );

  const abrirPainel = async (phoneId) => {
    setAbrindo(phoneId ?? "novo");
    try {
      const [catalogo, telefones] = await Promise.all([
        contas ? null : getAgentCatalog(),
        phoneId ? getAgentPhones({ client_id: cliente.id }) : null,
      ]);
      if (catalogo) setContas(catalogo.contas || []);
      const contato = phoneId
        ? (telefones?.telefones || []).find((item) => item.id === phoneId)
        : null;
      setPainel({ aberto: true, contato: contato || null });
    } catch (err) {
      notification.error({
        message: "Erro ao abrir o contato.",
        description: describeError(err, ""),
      });
    } finally {
      setAbrindo(null);
    }
  };

  const contatoSalvo = (salvo, mensagem) => {
    const contato = noFormatoDoCatalogo(salvo);
    setSalvos((atuais) => [
      ...atuais.filter((item) => item.id !== contato.id),
      contato,
    ]);
    if (!painel.contato) {
      const linha = {
        chave: `id:${contato.id}`,
        phone: contato.id,
        nome: contato.nome,
        telefone: contato.telefone,
        ativo: contato.ativo,
        filiaisDoContato: contato.filiais,
      };
      onChange(
        comFiliais(
          inscricoes,
          linha,
          filiaisPossiveis(contato.filiais, idsDaRotina)
        )
      );
      setRecente(linha.chave);
    }
    setPainel({ aberto: false, contato: null });
    notification.success({
      message: painel.contato ? mensagem : "Contato criado e inscrito.",
    });
    if (onContatoSalvo) onContatoSalvo();
  };

  const possiveisDaLinha = (linha) =>
    filiaisPossiveis(linha.filiaisDoContato, idsDaRotina);
  const marcadasDaLinha = (linha) => {
    const possiveis = new Set(possiveisDaLinha(linha));
    return (porChave.get(linha.chave)?.filiais || []).filter((id) =>
      possiveis.has(id)
    );
  };

  const alternarCelula = (linha, filialId, marcar) => {
    const atuais = marcadasDaLinha(linha);
    onChange(
      comFiliais(
        inscricoes,
        linha,
        marcar ? [...atuais, filialId] : atuais.filter((id) => id !== filialId)
      )
    );
  };

  const alternarColuna = (filialId) => {
    const elegiveis = linhas.filter((linha) =>
      possiveisDaLinha(linha).includes(filialId)
    );
    const todas = elegiveis.every((linha) =>
      marcadasDaLinha(linha).includes(filialId)
    );
    let proximas = inscricoes;
    for (const linha of elegiveis) {
      const atuais = marcadasDaLinha(linha);
      const filiaisDaLinha = todas
        ? atuais.filter((id) => id !== filialId)
        : [...new Set([...atuais, filialId])];
      proximas = comFiliais(proximas, linha, filiaisDaLinha);
    }
    onChange(proximas);
  };

  const colunas = [
    {
      title: "Contato",
      key: "contato",
      fixed: "left",
      render: (_, linha) => {
        const possiveis = possiveisDaLinha(linha);
        return (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Checkbox
              checked={porChave.has(linha.chave)}
              disabled={!possiveis.length}
              aria-label={`${linha.nome} recebe`}
              onChange={(e) =>
                onChange(
                  comFiliais(
                    inscricoes,
                    linha,
                    e.target.checked ? possiveis : []
                  )
                )
              }
            />
            <div>
              {linha.phone && cliente ? (
                <a
                  href={`/contatos?cliente=${cliente.id}&contato=${linha.phone}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontWeight: 500 }}>
                  {linha.nome}
                </a>
              ) : (
                <Text style={{ fontWeight: 500 }}>{linha.nome}</Text>
              )}
              <Text type="secondary" style={{ marginLeft: 10 }}>
                {formatarTelefone(linha.telefone)}
              </Text>
              {!possiveis.length && (
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: 13 }}>
                  Sem filial nesta rotina
                  {linha.phone && cliente && !linha.doUsuario && (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: "auto", marginLeft: 8 }}
                      loading={abrindo === linha.phone}
                      onClick={() => abrirPainel(linha.phone)}>
                      Associar a uma filial
                    </Button>
                  )}
                </Text>
              )}
              {!possiveis.length && linha.doUsuario && (
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: 13 }}>
                  As filiais vêm do usuário no app. Mude no Painel de Controle.
                </Text>
              )}
              {!linha.ativo && (
                <Text
                  type="secondary"
                  style={{ display: "block", fontSize: 13 }}>
                  Contato inativo: não recebe
                </Text>
              )}
            </div>
          </div>
        );
      },
    },
    ...filiais.map((filial) => ({
      title: (
        <Button
          type="text"
          size="small"
          title={`${filial.nome}: marcar ou desmarcar todos`}
          onClick={() => alternarColuna(filial.id)}>
          {filial.rotulo}
        </Button>
      ),
      key: `filial-${filial.id}`,
      width: 104,
      align: "center",
      render: (_, linha) =>
        possiveisDaLinha(linha).includes(filial.id) ? (
          <Checkbox
            checked={marcadasDaLinha(linha).includes(filial.id)}
            aria-label={`${linha.nome} recebe ${filial.nome}`}
            onChange={(e) => alternarCelula(linha, filial.id, e.target.checked)}
          />
        ) : null,
    })),
  ];

  const total = inscricoes.length;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 8,
          gap: 12,
          flexWrap: "wrap",
        }}>
        <Text type="secondary">
          As colunas são as filiais da rotina, escolhidas no passo 1.
        </Text>
        <Space size={12} wrap>
          <Text type="secondary">
            {total === 1
              ? "1 contato, 1 mensagem por envio"
              : `${total} contatos, ${total} mensagens por envio`}
          </Text>
          <Button
            disabled={!filiais.length || !cliente}
            loading={abrindo === "novo"}
            onClick={() => abrirPainel(null)}>
            Novo contato
          </Button>
        </Space>
      </div>
      <Table
        bordered
        size="middle"
        rowKey="chave"
        columns={colunas}
        dataSource={linhas}
        pagination={false}
        scroll={{ x: "max-content", y: 420 }}
        rowClassName={(linha) =>
          [
            porChave.has(linha.chave) ? "destinatario-inscrito" : "",
            recente === linha.chave ? "destinatario-recente" : "",
          ]
            .filter(Boolean)
            .join(" ")
        }
        locale={{
          emptyText:
            "O cliente ainda não tem contatos. Crie um em Novo contato.",
        }}
      />
      {cliente && (
        <PainelDoContato
          aberto={painel.aberto}
          contato={painel.contato}
          cliente={cliente}
          filiais={filiaisDoCliente || []}
          contas={contas || []}
          filiaisIniciais={idsDaRotina}
          compacto
          comMascara
          salvarRotulo="Criar e inscrever"
          contexto={
            painel.contato
              ? `Cliente: ${cliente.name}`
              : "Entra nesta rotina, inscrito nas filiais marcadas"
          }
          aviso={
            painel.contato
              ? "As filiais marcadas aqui valem para todas as rotinas. Depois de salvar, marque na grade de quais filiais ele recebe esta."
              : "O contato fica no cadastro do cliente e aparece em Contatos. Desmarque as filiais que ele não deve receber."
          }
          onFechar={() => setPainel({ aberto: false, contato: null })}
          onSalvo={contatoSalvo}
          onExcluido={() => {
            setPainel({ aberto: false, contato: null });
            if (onContatoSalvo) onContatoSalvo();
          }}
        />
      )}
    </div>
  );
}
